/**
 * CRO Audit System - Main Application
 * Entry point and orchestrator for the audit report application
 */

import { DataService, CheckService, CardRenderService } from './services';
import { CardComponent, ScoreComponent, LightboxComponent } from './components';
import {
  FilterManager,
  SortManager,
  PageManager,
  ImplementationButtonManager,
  FeedbackManager
} from './ui';
import { AuditData, PageType, DeviceData, MapData } from './types';
import { calcOverall, formatDate } from './utils';
import { SEMANTIC_COLORS } from './design-system/colors';

/**
 * Main CRO Audit Application
 * Orchestrates services, managers, and components
 */
class CROAuditApp {
  // Services
  private dataService = new DataService();
  private checkService = new CheckService();
  private cardRenderService: CardRenderService;

  // Components
  private scoreComponent = new ScoreComponent();
  private lightboxComponent = new LightboxComponent();

  // UI Managers
  private filterManager = new FilterManager();
  private sortManager = new SortManager();
  private pageManager = new PageManager();
  private implementationButtonManager = new ImplementationButtonManager();
  private feedbackManager = new FeedbackManager();

  // State
  private auditName: string = '';
  private allData: Record<PageType, AuditData | null> = {
    product: null,
    collection: null,
    home: null,
    cart: null
  };
  private mapData: MapData | null = null;

  constructor() {
    // Initialize card component with feedback manager's card actions
    const cardComponent = new CardComponent(
      this.feedbackManager.getCardActionsComponent()
    );

    // Initialize card render service
    this.cardRenderService = new CardRenderService(cardComponent);
  }

  /**
   * Initialize the application
   */
  async initialize(): Promise<void> {
    try {
      // Get audit name from URL
      this.auditName = this.getAuditName();

      // Initialize feedback manager with audit name
      this.feedbackManager.initialize(this.auditName);

      // Load data
      await this.loadData();

      // Setup UI
      this.setupUI();

      // Setup global handlers
      this.setupGlobalHandlers();

      // Initial render
      await this.render();
    } catch (error) {
      console.error('Error initializing application:', error);
      this.showError('Failed to initialize application');
    }
  }

  /**
   * Get audit name from URL parameters
   */
  private getAuditName(): string {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('audit') || 'example';
  }

  /**
   * Load all required data
   */
  private async loadData(): Promise<void> {
    const [pagesResult, mapData] = await Promise.all([
      this.dataService.fetchAllPages(this.auditName),
      this.dataService.fetchMapData()
    ]);

    this.allData = pagesResult.data;
    this.mapData = mapData;

    // Initialize page manager with existence data
    this.pageManager.initialize(pagesResult.existence);
  }

  /**
   * Setup UI managers
   */
  private setupUI(): void {
    // Initialize managers
    this.filterManager.initialize();
    this.sortManager.initialize();
    this.implementationButtonManager.initialize(this.auditName);

    // Register callbacks
    this.pageManager.onPageChange(() => this.handlePageChange());
    this.pageManager.onDeviceFilterChange((filter) => this.handleDeviceFilterChange(filter));

    // Setup accordion click handler
    this.setupAccordionHandler();

    // Initialize filter buttons
    const allFilterBtn = document.querySelector('[data-filter="all"]') as HTMLButtonElement;
    if (allFilterBtn) {
      allFilterBtn.click();
    }

    // Initialize sort buttons
    const confidenceSortBtn = document.querySelector('[data-sort="confidence"]') as HTMLButtonElement;
    if (confidenceSortBtn) {
      confidenceSortBtn.classList.add('ring-2', 'ring-offset-2');
    }
  }

  /**
   * Setup global handlers for lightbox
   */
  private setupGlobalHandlers(): void {
    (window as any).openLightbox = (src: string, alt: string) => {
      this.lightboxComponent.open(src, alt);
    };

    (window as any).closeLightbox = () => {
      this.lightboxComponent.close();
    };
  }

  /**
   * Setup accordion click handler
   */
  private setupAccordionHandler(): void {
    const container = document.getElementById('recommendations-container');
    if (!container) return;

    container.addEventListener('click', (e) => {
      const toggleButton = (e.target as HTMLElement).closest('[data-toggle="true"]');
      if (!toggleButton) return;

      const toggle = toggleButton.closest('.accordion-toggle');
      if (!toggle) return;

      const card = toggle.parentElement;
      const content = toggle.nextElementSibling as HTMLElement;
      const chevron = toggleButton.querySelector('.chevron-icon');

      if (!card || !content) return;

      card.classList.toggle('bg-gray-800');
      chevron?.classList.toggle('rotate-180');

      if (content.classList.contains('expanded')) {
        content.classList.remove('expanded');
        content.style.maxHeight = '';
      } else {
        content.classList.add('expanded');
        content.style.maxHeight = content.scrollHeight + 'px';
      }
    });
  }

  /**
   * Handle page type change
   */
  private async handlePageChange(): Promise<void> {
    await this.render();
  }

  /**
   * Handle device filter change
   */
  private handleDeviceFilterChange(filter: string): void {
    this.filterManager.setDeviceFilter(filter as any);
  }

  /**
   * Main render function
   */
  private async render(): Promise<void> {
    const currentPageType = this.pageManager.getCurrentPageType();
    const pageData = this.allData[currentPageType];

    // Check if page exists
    if (!pageData) {
      this.showUnavailablePage();
      return;
    }

    // Get both mobile and desktop data
    const mobileData = this.normalizeDeviceData(pageData.mobile);
    const desktopData = this.normalizeDeviceData(pageData.desktop);

    // Check if any data exists
    if (!mobileData && !desktopData) {
      this.showNoData();
      return;
    }

    // Combine checks and process into actions
    const combinedChecks = this.checkService.combineChecks(mobileData, desktopData);
    const actions = this.checkService.processActions(combinedChecks);
    const topICE = this.checkService.getTopICEItems(actions, 3);

    // Update header
    this.updateHeader(pageData, mobileData, desktopData);

    // Render cards using card render service
    const renderResult = this.cardRenderService.renderCards(actions, topICE, this.mapData);

    // Attach feedback handlers to rendered cards
    const container = document.getElementById('recommendations-container');
    if (container) {
      this.feedbackManager.attachCardActionHandlers(container);
    }

    // Add empty state cards
    this.cardRenderService.addEmptyStateCards(actions);

    // Update implementation button
    this.implementationButtonManager.update(renderResult.total);

    // Reapply sort
    this.sortManager.reapplySort();

    // Update feedback button
    this.feedbackManager.updateEmailReportButton();

    // Apply filters
    this.filterManager.applyFilters();
  }

  /**
   * Normalize device data that might be returned as an empty object
   */
  private normalizeDeviceData(deviceData: DeviceData | null): DeviceData | null {
    if (!deviceData) return null;

    const hasContent = ['visualAnalysis', 'copyAnalysis', 'performanceAnalysis', 'seoAnalysis', 'accessibilityAnalysis']
      .some((key) => {
        const analysis = (deviceData as any)[key];
        return analysis && Object.keys(analysis).length > 0;
      });

    return hasContent ? deviceData : null;
  }

  /**
   * Collect numeric score values from a device
   */
  private collectDeviceScores(deviceData: DeviceData | null): number[] {
    if (!deviceData) return [];

    const possibleScores = [
      (deviceData as any)?.visualAnalysis?.visualScore,
      (deviceData as any)?.copyAnalysis?.copyScore,
      (deviceData as any)?.performanceAnalysis?.performanceScore,
      (deviceData as any)?.seoAnalysis?.seoScore,
      (deviceData as any)?.accessibilityAnalysis?.accessibilityScore
    ];

    return possibleScores.filter(
      (value): value is number => typeof value === 'number' && Number.isFinite(value)
    );
  }

  /**
   * Update header information
   */
  private updateHeader(
    pageData: AuditData,
    mobileData: DeviceData | null,
    desktopData: DeviceData | null
  ): void {
    // Update URL
    const urlElement = document.getElementById('page-url') as HTMLAnchorElement;
    if (urlElement) {
      urlElement.href = pageData.link || '';
      urlElement.textContent = pageData.link || '';
    }

    // Calculate and render overall score
    const overall = calcOverall([
      ...this.collectDeviceScores(mobileData),
      ...this.collectDeviceScores(desktopData)
    ]);
    this.scoreComponent.render('score-display', overall);

    // Update dates
    const today = formatDate();
    const lastUpdatedEl = document.getElementById('last-updated');
    const footerDateEl = document.getElementById('footer-date');
    if (lastUpdatedEl) lastUpdatedEl.textContent = today;
    if (footerDateEl) footerDateEl.textContent = today;
  }

  /**
   * Show unavailable page message
   */
  private showUnavailablePage(): void {
    const mailtoLink = `mailto:vincent@actionbase.ai?subject=${encodeURIComponent(this.auditName + ': I am interested to see full report.')}`;
    const container = document.getElementById('recommendations-container');
    if (!container) return;

    container.innerHTML = `
      <div class="bg-gray-800/50 border border-gray-700 rounded-lg p-8 text-center" data-report-unavailable="true">
        <div class="text-gray-300">
          <svg class="w-16 h-16 mx-auto mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
          </svg>
          <h3 class="text-xl font-semibold mb-2">Unlock full-store insights</h3>
          <p class="text-sm text-gray-400 mb-6">Get CRO reports for your Homepage, Collection, and Cart pages.</p>
          <a href="${mailtoLink}" class="inline-flex items-center gap-2 px-6 py-3 ${SEMANTIC_COLORS.cta.primary} font-medium rounded-lg transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
            Request Full Audit
          </a>
        </div>
      </div>
    `;

    this.scoreComponent.clear('score-display');
  }

  /**
   * Show no data message
   */
  private showNoData(): void {
    const container = document.getElementById('recommendations-container');
    if (!container) return;

    container.innerHTML = `
      <div class="${SEMANTIC_COLORS.alerts.warning.background} border rounded-lg p-6 text-center">
        <div class="${SEMANTIC_COLORS.alerts.warning.text}">
          <svg class="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
          </svg>
          <p class="text-sm font-medium">No data available</p>
        </div>
      </div>
    `;

    this.scoreComponent.clear('score-display');
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    const container = document.getElementById('recommendations-container');
    if (!container) return;

    container.innerHTML = `
      <div class="${SEMANTIC_COLORS.alerts.error.background} border rounded-lg p-6 text-center">
        <div class="${SEMANTIC_COLORS.alerts.error.text}">
          <svg class="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
          </svg>
          <p class="text-sm font-medium">${message}</p>
        </div>
      </div>
    `;
  }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const app = new CROAuditApp();
  app.initialize();
});
